import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, NavLink, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faTrophy,
  faChartBar,
  faUser,
  faCog,
  faSignOutAlt,
  faBell,
  faBars,
  faTimes,
  faCoins,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { useGlobalSocket } from '../../hooks/useGlobalSocket';
import socketService from '../../services/socketService';
import { NotificationPanel } from '../ui/NotificationPanel';
import { BottomNavigation, NavItem } from '../ui/BottomNavigation';
import logo from '../../assets/images/logo.png';

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { unreadCount } = useNotificationStore();

  // Connect global socket for real-time notifications
  useGlobalSocket();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user dropdown on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  const primaryNav = [
    { label: 'Home', path: '/home' },
    { label: 'Contests', path: '/my-contests' },
    { label: 'Leaderboard', path: '/leaderboard' },
  ];

  const bottomNavItems: NavItem[] = [
    { id: 'home', label: 'Home', icon: <FontAwesomeIcon icon={faHome} className="text-xl" />, path: '/home' },
    { id: 'contests', label: 'Contests', icon: <FontAwesomeIcon icon={faTrophy} className="text-xl" />, path: '/my-contests' },
    { id: 'profile', label: 'Profile', icon: <FontAwesomeIcon icon={faUser} className="text-xl" />, path: '/profile' },
  ];

  const queryClient = useQueryClient();
  const resetNotifications = useNotificationStore((s) => s.reset);

  const handleLogout = () => {
    // Clear all cached data from previous user
    queryClient.clear();
    resetNotifications();
    socketService.disconnect();
    logout();
    navigate('/');
  };

  const userInitials = user
    ? ((user.firstName?.[0] || '') + (user.lastName?.[0] || '')).toUpperCase() || 'U'
    : 'U';

  return (
    <div className="min-h-screen bg-[#0F1114] flex flex-col">
      {/* Top Navigation Bar */}
      <header className="bg-[#0F1114]/95 backdrop-blur-lg border-b border-[#2E3340] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* LEFT: Logo */}
            <div className="flex items-center gap-3 cursor-pointer shrink-0" onClick={() => navigate('/home')}>
              <img src={logo} alt="CricClash" className="w-9 h-9 rounded-lg object-cover" />
              <span className="text-lg font-bold text-white hidden sm:block">CricClash</span>
            </div>

            {/* CENTER: Nav tabs */}
            <nav className="hidden lg:flex items-center space-x-8">
              {primaryNav.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `relative py-5 text-sm font-medium transition-colors ${
                      isActive ? 'text-[#13EC5B]' : 'text-gray-400 hover:text-white'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span>{item.label}</span>
                      {isActive && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#13EC5B] rounded-full" />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* RIGHT: Actions */}
            <div className="flex items-center space-x-2">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => { setShowNotifications(!showNotifications); setShowUserMenu(false); }}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-all relative"
                >
                  <FontAwesomeIcon icon={faBell} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#13EC5B] text-black text-[9px] font-bold px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <NotificationPanel onClose={() => setShowNotifications(false)} />
                )}
              </div>

              {/* User dropdown */}
              <div className="hidden lg:block relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-white/5 transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-[#13EC5B]/10 border border-[#13EC5B]/30 flex items-center justify-center text-[#13EC5B] text-xs font-bold">
                    {userInitials}
                  </div>
                  <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-[10px]" />
                </button>

                {/* Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-[#1A1D23] border border-[#2E3340] rounded-xl shadow-2xl shadow-black/40 overflow-hidden animate-scale-in origin-top-right z-50">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-[#2E3340]">
                      <p className="text-sm font-medium text-white truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>

                    <div className="py-1">
                      <NavLink
                        to="/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <FontAwesomeIcon icon={faUser} className="w-4 text-gray-500" />
                        Profile
                      </NavLink>
                      {/* Credits — hidden for now */}
                      {/* <NavLink
                        to="/credits"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <FontAwesomeIcon icon={faCoins} className="w-4 text-gray-500" />
                        Credits
                      </NavLink> */}
                      <NavLink
                        to="/settings"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <FontAwesomeIcon icon={faCog} className="w-4 text-gray-500" />
                        Settings
                      </NavLink>
                    </div>

                    <div className="border-t border-[#2E3340] py-1">
                      <button
                        onClick={() => { setShowUserMenu(false); handleLogout(); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-300 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <FontAwesomeIcon icon={faSignOutAlt} className="w-4 text-gray-500" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-all"
              >
                {showMobileMenu ? <FontAwesomeIcon icon={faTimes} /> : <FontAwesomeIcon icon={faBars} />}
              </button>
            </div>
          </div>

          {/* Mobile Dropdown Menu */}
          {showMobileMenu && (
            <div className="lg:hidden py-3 border-t border-[#2E3340]">
              <div className="space-y-1">
                {primaryNav.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setShowMobileMenu(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium text-sm ${
                        isActive
                          ? 'bg-[#13EC5B]/10 text-[#13EC5B]'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <div className="border-t border-[#2E3340] my-2" />
                <NavLink
                  to="/profile"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5"
                >
                  <FontAwesomeIcon icon={faUser} className="w-4" />
                  Profile
                </NavLink>
                <NavLink
                  to="/settings"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5"
                >
                  <FontAwesomeIcon icon={faCog} className="w-4" />
                  Settings
                </NavLink>
                <button
                  onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-sm text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="w-4" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="hidden lg:block border-t border-[#2E3340] bg-[#0F1114]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            {/* Brand */}
            <div className="max-w-xs">
              <div className="flex items-center gap-2 mb-3">
                <img src={logo} alt="CricClash" className="w-8 h-8 rounded-lg object-cover" />
                <span className="text-white font-bold">CricClash</span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                The ultimate fantasy cricket platform. Build your dream team and compete with friends.
              </p>
            </div>

            {/* Links */}
            <div className="flex gap-12">
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Product</h4>
                <div className="space-y-2">
                  <Link to="/how-to-play" className="block text-sm text-gray-500 hover:text-[#13EC5B] transition-colors">How to Play</Link>
                  <Link to="/faq" className="block text-sm text-gray-500 hover:text-[#13EC5B] transition-colors">FAQ</Link>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Legal</h4>
                <div className="space-y-2">
                  <Link to="/privacy-policy" className="block text-sm text-gray-500 hover:text-[#13EC5B] transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="block text-sm text-gray-500 hover:text-[#13EC5B] transition-colors">Terms of Service</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-8 pt-6 border-t border-[#2E3340] flex items-center justify-between">
            <p className="text-xs text-gray-600">&copy; {new Date().getFullYear()} CricClash. All rights reserved.</p>
            <div className="flex gap-4">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#13EC5B] transition-colors text-sm">Twitter</a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#13EC5B] transition-colors text-sm">Instagram</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom Navigation - Mobile only */}
      <BottomNavigation items={bottomNavItems} />
    </div>
  );
};

export default MainLayout;
